import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';
import { toastWarning } from '@components/Toast';

import { generateYears } from '@features/crm/utils/helpers';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	fetchBrandBasicList,
	fetchDepartments,
	fetchEnumValues,
	getOrganizationCountryList
} from '@services/commonService';
import { fetchTargetTeamMemberList, setCRMTarget, updateCRMTarget } from '@services/crmService';

import { formatNumberWithSuffix } from '@utils/helper';
import {
	convertNumberToUSFormat,
	getNumberUnit,
	getUserInfo,
	isCRMManagement,
	isDirector,
	validateFloatNumber,
	validateNumber
} from '@utils/Util';

const SetTarget = ({ fetchAllTargets, selectedTarget, clearSelection, isAssignedClick, isEditClick }) => {
	const [selectedBrands, setSelectedBrands] = useState([]);
	const [inputData, setInputData] = useState({});
	const [quarters, setQuarters] = useState([]);
	const [markets, setMarkets] = useState([]);
	const [regions, setRegions] = useState([]);
	const [members, setMembers] = useState([]);
	const [brands, setBrands] = useState([]);
	const [errors, setErrors] = useState({});
	const [years, setYears] = useState([]);
	const dispatch = useDispatch();

	const getTarget = (selectedTarget) => {
		let result = 0;
		if (isAssignedClick) {
			let target = (selectedTarget?.targetAmount || 0) - (selectedTarget?.assignedAmount || 0);
			if (target > 0) {
				result = target;
			}
		} else {
			result = selectedTarget.targetAmount;
		}
		return result;
	};

	const getNitexDesign = (selectedTarget) => {
		let result = 0;
		if (isAssignedClick) {
			let target =
				(selectedTarget?.targetNoOfNitexDesign || 0) - (selectedTarget?.assignedNoOfNitexDesign || 0);
			if (target > 0) {
				result = target;
			}
		} else {
			result = selectedTarget.targetNoOfNitexDesign;
		}
		return result;
	};

	useEffect(() => {
		if (selectedTarget !== null) {
			let prevData = {};
			const {
				targetSetTo,
				id,
				targetAmount,
				targetNoOfNitexDesign,
				brand,
				productGroup,
				immediateParentInfo
			} = selectedTarget;

			const setPrevData = (source) => {
				if (source?.country?.id) {
					prevData['region'] = { id: source.country.id, value: source.country.name };
				}
				if (source?.year) {
					prevData['year'] = { id: source.year, value: source.year };
				}
				if (source?.quarter) {
					prevData['quarter'] = { id: source.quarter, value: source.quarter };
				}
				if (source?.targetAmount) {
					prevData['target'] = getTarget(source);
				}
				if (source?.targetNoOfNitexDesign) {
					prevData['nitexDesign'] = getNitexDesign(source);
				}
				if (source?.brand?.id) {
					prevData['brand'] = { id: source.brand.id, value: source.brand.name };
				}
				if (source?.productGroup?.id) {
					prevData['market'] = { id: source.productGroup.id, value: source.productGroup.name };
				}
			};

			if (targetSetTo?.id !== getUserInfo()?.id) {
				prevData['teamMember'] = { id: targetSetTo.id, value: targetSetTo.name };
			}

			if (id) {
				setPrevData(selectedTarget);
			} else {
				setPrevData(immediateParentInfo);
			}

			if (isAssignedClick && brand?.id && isShowAddBrands()) {
				let tmpObj = {
					brand: { id: brand.id, value: brand.name },
					target: targetAmount || '',
					nitexDesign: targetNoOfNitexDesign || '',
					market: productGroup?.id ? { id: productGroup.id, value: productGroup.name } : {}
				};
				setSelectedBrands([tmpObj]);
			}

			setInputData(prevData);
		}
	}, [selectedTarget]);

	const clearFields = () => {
		setInputData({});
		setSelectedBrands([]);
		setErrors({});
		clearSelection();
	};

	const fetchAllMembers = async (params) => {
		let workflowMembers = await fetchTargetTeamMemberList(params);
		workflowMembers = workflowMembers?.map((item) => ({ value: item.name, id: item.id }));
		setMembers(workflowMembers);
	};

	const fetchAllQuarters = async () => {
		let quarterList = await fetchEnumValues('info.nitex.app.crm.QuarterType');
		quarterList = quarterList?.map((item) => ({ value: item.name, id: item.constant }));
		setQuarters(quarterList);
	};

	const fetchAllBrands = async () => {
		let brandList = await fetchBrandBasicList(`?size=1000&page=0`);
		brandList = brandList?.map((item) => ({ value: item.name, id: item.id }));
		setBrands(brandList);
	};

	const fetchAllDepartments = async () => {
		let departmentList = await fetchDepartments(`?size=1000&page=0`);
		departmentList = departmentList?.map((item) => ({ value: item.name, id: item.id }));
		setMarkets(departmentList);
	};

	const fetchAllRegions = async () => {
		let regionList = await getOrganizationCountryList();
		regionList = regionList?.map((item) => ({ value: item.name, id: item.id }));
		setRegions(regionList);
	};

	useEffect(() => {
		const yearList = generateYears(2024, 2030);
		setYears(yearList?.map((item) => ({ id: item, value: item })));
		(async () => {
			try {
				dispatch(openLoader());
				await Promise.all([
					fetchAllMembers(),
					fetchAllQuarters(),
					fetchAllBrands(),
					fetchAllDepartments(),
					fetchAllRegions()
				]);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		})();
	}, []);

	const addNewBrand = () => {
		if (selectedBrands.length === 0) {
			setInputData((prev) => ({ ...prev, target: '', nitexDesign: '' }));
		}
		setSelectedBrands((prev) => [
			...prev,
			{
				brand: {},
				market: {},
				target: '',
				nitexDesign: ''
			}
		]);
	};

	const removeBrand = (index) => {
		let modifiedBrands = selectedBrands.filter((item, i) => i !== index);
		setSelectedBrands(modifiedBrands);
	};

	const onChangeHandler = (e) => {
		const { name, value } = e.target;
		setInputData((prev) => ({ ...prev, [name]: value }));
	};

	const renderSelectedBrands = () => {
		return selectedBrands?.map((item, idx) => {
			return (
				<div className="brand-group mb-3" key={`brand_${idx}`}>
					<div className="d-flex justify-content-between mb-2">
						<p className="semibold-14">Brand {idx + 1}</p>
						<img
							src={`${process.env.PUBLIC_URL}/icons/delete_red.svg`}
							alt="delete"
							className="cursor-pointer"
							onClick={() => removeBrand(idx)}
						/>
					</div>
					<div className="container px-0">
						<div className="row">
							<div className="col-md-6 pr-6">
								<div className="form-group mb-2">
									<label htmlFor="brand">Brand Name</label>
									<CustomDropdown
										type="addItem"
										title="brand"
										onItemClick={(data) => handleItemClick(data, idx)}
										items={brands}
										selectedItem={item?.brand?.value || 'Select Brand'}
									/>
									{errors?.[`brand_${idx}`] && (
										<p className="error">{errors[`brand_${idx}`]}</p>
									)}
								</div>
							</div>
							<div className="col-md-6 pl-6">
								<div className="form-group mb-2">
									<label htmlFor="market">Market (Optional)</label>
									<CustomDropdown
										type="addItem"
										title="market"
										onItemClick={(data) => handleItemClick(data, idx)}
										items={markets}
										selectedItem={item?.market?.value || 'Select Market'}
									/>
									{errors?.[`market_${idx}`] && (
										<p className="error">{errors[`market_${idx}`]}</p>
									)}
								</div>
							</div>
							<div className="col-md-6 pr-6">
								<div className="form-group mb-2">
									<label htmlFor="brand">Target</label>
									<input
										type="number"
										name="target"
										value={item?.target}
										onChange={(e) => handleBrandWiseInputChange(e, idx)}
										onKeyPress={validateFloatNumber}
										min={0}
									/>
									{errors?.[`target_${idx}`] && (
										<p className="error">{errors[`target_${idx}`]}</p>
									)}
								</div>
							</div>
							<div className="col-md-6 pl-6">
								<div className="form-group mb-2">
									<label htmlFor="brand">Nitex Design</label>
									<input
										type="number"
										name="nitexDesign"
										value={item?.nitexDesign}
										onChange={(e) => handleBrandWiseInputChange(e, idx)}
										onKeyPress={validateNumber}
										min={0}
									/>
									{errors?.[`nitexDesign_${idx}`] && (
										<p className="error">{errors[`nitexDesign_${idx}`]}</p>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			);
		});
	};

	const isAddBrandClickable = () => {
		if (selectedBrands.length > 0) {
			let lastAddedBrand = selectedBrands[selectedBrands.length - 1];
			return lastAddedBrand.brand?.value && lastAddedBrand.target && lastAddedBrand.nitexDesign;
		}
		return true;
	};

	const handleItemClick = (data, idx) => {
		let modifiedSelectedBrands = selectedBrands?.map((item, i) => {
			if (i === idx) {
				item[data.title] = { value: data.value, id: data.id };
			}
			return item;
		});
		setSelectedBrands(modifiedSelectedBrands);
	};

	const handleBrandWiseInputChange = (e, idx) => {
		const { name, value } = e.target;
		let modifiedSelectedBrands = selectedBrands?.map((item, i) => {
			if (i === idx) {
				item[name] = value;
			}
			return item;
		});
		setSelectedBrands(modifiedSelectedBrands);
	};

	const onItemClick = async (data) => {
		if (data.title === 'region') {
			await fetchAllMembers(`?countryId=${data.id}`);
		}
		setInputData((prev) => ({ ...prev, [data.title]: { id: data.id, value: data.value } }));
	};

	const getTotalValue = () => {
		let target = 0;
		let nitexDesign = 0;
		if (selectedBrands.length === 0) {
			target = inputData.target || 0;
			nitexDesign = inputData.nitexDesign || 0;
		} else {
			selectedBrands.forEach((item) => {
				target += item.target ? parseInt(item.target) : 0;
				nitexDesign += item.nitexDesign ? parseInt(item.nitexDesign) : 0;
			});
		}
		return {
			target: target,
			nitexDesign: nitexDesign
		};
	};

	const isValid = () => {
		let err = {};
		if (!inputData?.region?.id && isCRMManagement()) {
			err['region'] = 'Region is required';
		}
		if (!inputData?.teamMember?.id) {
			err['teamMember'] = 'Member is required';
		}
		if (!inputData?.year?.id) {
			err['year'] = 'Year is required';
		}
		if (!inputData?.target && selectedBrands.length === 0) {
			err['target'] = 'Target is required';
		}
		if (!inputData?.nitexDesign && selectedBrands.length === 0) {
			err['nitexDesign'] = 'Design is required';
		}
		selectedBrands.forEach((item, i) => {
			if (!item?.brand?.id) {
				err[`brand_${i}`] = 'Brand is required';
			}
			if (!item?.target) {
				err[`target_${i}`] = 'Target is required';
			}
			if (!item?.nitexDesign) {
				err[`nitexDesign_${i}`] = 'Design is required';
			}
		});
		setErrors(err);
		return Object.keys(err).length === 0;
	};
	const getNewAmountOrStyle = (type) => {
		let result = 0;
		if (selectedBrands.length > 0) {
			selectedBrands.map((item) => {
				result += item[type];
			});
		} else {
			result = inputData[type];
		}
		return result;
	};

	const handleSubmit = async () => {
		if (!isValid()) return;

		if (
			(Math.sign(getRemainingAmount(getNewAmountOrStyle('target'), false)) !== -1 &&
				Math.sign(getRemainingDesign(getNewAmountOrStyle('nitexDesign'), false)) !== -1) ||
			selectedTarget === null
		) {
			try {
				dispatch(openLoader());
				const userInfo = getUserInfo();
				const { region, teamMember, year, quarter, target, nitexDesign, brand, market } = inputData;
				const postData = {
					countryId: region?.id || userInfo?.country?.id,
					targetSetToId: teamMember?.id,
					year: year?.id,
					quarter: quarter?.id
				};

				const createBrandWiseTargetList = () => {
					if (selectedBrands.length > 0) {
						return selectedBrands.map((item) => ({
							brandId: item.brand?.id,
							productGroupId: item.market?.id,
							targetAmount: item.target,
							targetNoOfNitexDesign: item.nitexDesign
						}));
					}
					return [
						{
							brandId: brand?.id,
							productGroupId: market?.id,
							targetAmount: target,
							targetNoOfNitexDesign: nitexDesign
						}
					];
				};

				if (selectedBrands.length === 0) {
					postData.targetAmount = target;
					postData.targetNoOfNitexDesign = nitexDesign;
				} else {
					postData.brandWiseTargetRequestList = createBrandWiseTargetList();
				}

				if (selectedTarget?.id && !isAssignedClick) {
					if (brand?.id) postData.brandId = brand.id;
					if (market?.id) postData.productGroupId = market.id;
					await updateCRMTarget(selectedTarget.id, postData);
				} else {
					if (isAssignedClick) {
						postData.parentTargetId = selectedTarget?.id || selectedTarget?.parentTargetId;
					}
					if (isAssignedClick && selectedBrands.length === 0) {
						postData.brandWiseTargetRequestList = createBrandWiseTargetList();
					}
					if (selectedBrands.length === 0 && !brand?.id) {
						delete postData.brandWiseTargetRequestList;
					}
					await setCRMTarget(postData);
				}

				await fetchAllTargets();
				clearFields();
				closeModal();
			} finally {
				dispatch(closeLoader());
			}
		} else {
			toastWarning('Target amount or style exceeds the assigned amount or style');
		}
	};

	const closeModal = () => {
		let button = document.getElementById('close-modal');
		button.click();
	};

	const isDisabled = (fieldName) => {
		if (selectedTarget?.id) {
			return !!selectedTarget?.[fieldName];
		} else if (selectedTarget?.immediateParentInfo?.id) {
			return !!selectedTarget?.immediateParentInfo?.[fieldName];
		} else {
			return false;
		}
	};

	const isFieldReadOnly = (fieldName) => {
		return !isAssignedClick && selectedTarget?.[fieldName] === false;
	};

	const isBrandOrMarketEditable = (fieldName, editableFlag) => {
		if (isAssignedClick) {
			if (selectedTarget?.id) {
				return !!selectedTarget?.[fieldName]?.id;
			} else {
				return !!selectedTarget?.immediateParentInfo?.[fieldName]?.id;
			}
		} else {
			return isFieldReadOnly(editableFlag);
		}
	};

	const getAmounts = (targetField, assignedField, isEditClick) => {
		let targetAmount = 0;
		let assignedAmount = 0;

		if (selectedTarget?.isRootNode || (selectedTarget?.id && !isEditClick)) {
			targetAmount = selectedTarget?.[targetField] || 0;
			assignedAmount = selectedTarget?.[assignedField] || 0;
		} else {
			targetAmount = selectedTarget?.immediateParentInfo?.[targetField] || 0;
			assignedAmount = selectedTarget?.immediateParentInfo?.[assignedField] || 0;
		}

		return { targetAmount, assignedAmount };
	};

	const calculateRemaining = (inputTarget, resultInUnit, targetField, assignedField, isEditClick) => {
		let { targetAmount, assignedAmount } = getAmounts(targetField, assignedField, isEditClick);

		// if (inputTarget > 0 && (targetField === 'targetAmount' || assignedField === 'assignedAmount')) {
		// 	inputTarget *= 1000;
		// }

		let result = targetAmount - assignedAmount - inputTarget;

		if (isEditClick && !selectedTarget?.isRootNode) {
			let increaseValue = selectedTarget?.id
				? selectedTarget?.[targetField] || 0
				: selectedTarget?.immediateParentInfo?.[targetField] || 0;
			result += increaseValue;
		}

		return resultInUnit ? getNumberUnit(result) : result;
	};

	const getRemainingAmount = (inputTarget = 0, resultInUnit = true) => {
		return calculateRemaining(inputTarget, resultInUnit, 'targetAmount', 'assignedAmount', isEditClick);
	};

	const getRemainingDesign = (inputTarget = 0, resultInUnit = true) => {
		return calculateRemaining(
			inputTarget,
			resultInUnit,
			'targetNoOfNitexDesign',
			'assignedNoOfNitexDesign',
			isEditClick
		);
	};

	const getParentNodeInfo = () => {
		return selectedTarget?.immediateParentInfo || {};
	};

	const isShowAddBrands = () => {
		if (selectedTarget === null) {
			return true;
		}
		if (isAssignedClick) {
			if (selectedTarget.isRootNode && !selectedTarget?.brand?.id) {
				return true;
			}
			if (!selectedTarget.isRootNode && !getParentNodeInfo()?.brand?.id) {
				return true;
			}
		}
		return false;
	};
	return (
		<div
			className="modal modal-right fade add-target-modal"
			id="add-target-modal"
			tabIndex={-1}
			role="dialog"
			aria-labelledby="right_modal"
			data-backdrop="static"
		>
			<div className="modal-dialog" role="document">
				<div className="modal-content">
					<div className="common-popup">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Set Target</h3>
							</div>
							<div
								id="close-modal"
								className="close-btn"
								data-dismiss="modal"
								aria-label="Close"
								aria-hidden="true"
								onClick={clearFields}
							>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body px-0">
							<div className="px-4 mb-3">
								<div className="container px-0">
									<div className="row">
										{isCRMManagement() && (
											<div className="col-md-6 pr-6">
												<div className="form-group mb-2">
													<label htmlFor="region">Region</label>
													<CustomDropdown
														type="addItem"
														title="region"
														onItemClick={onItemClick}
														items={regions}
														selectedItem={
															inputData?.region?.value || 'Select Region'
														}
													/>
													{errors?.region && (
														<p className="error">{errors.region}</p>
													)}
												</div>
											</div>
										)}
										<div
											className={`${
												isCRMManagement() ? 'col-md-6 pl-6' : 'col-md-12 pl-8'
											}`}
										>
											<div className="form-group mb-2">
												<label htmlFor="brand">Team Member</label>
												<CustomDropdown
													isDisabled={isFieldReadOnly('isTeamMemberEditable')}
													type="addItem"
													title="teamMember"
													onItemClick={onItemClick}
													items={members}
													selectedItem={
														inputData?.teamMember?.value || 'Select Member'
													}
												/>
												{errors?.teamMember && (
													<p className="error">{errors.teamMember}</p>
												)}
											</div>
										</div>
										<div className="col-md-6 pr-6">
											<div className="form-group mb-2">
												<label htmlFor="year">Year</label>
												<CustomDropdown
													isDisabled={
														isFieldReadOnly('isYearEditable') ||
														isDisabled('year')
													}
													type="addItem"
													title="year"
													onItemClick={onItemClick}
													items={years}
													selectedItem={inputData?.year?.value || 'Select Year'}
												/>
												{errors?.year && <p className="error">{errors.year}</p>}
											</div>
										</div>
										<div className="col-md-6 pl-6">
											<div className="form-group mb-2">
												<label htmlFor="quarter">Quarter (Optional)</label>
												<CustomDropdown
													isDisabled={
														isFieldReadOnly('isQuarterEditable') ||
														isDisabled('quarter')
													}
													type="addItem"
													title="quarter"
													onItemClick={onItemClick}
													items={quarters}
													selectedItem={
														inputData?.quarter?.value || 'Select Quarter'
													}
												/>
												{errors?.quarter && <p className="error">{errors.quarter}</p>}
											</div>
										</div>
										{!isShowAddBrands() && (
											<>
												<div className="col-md-6 pr-6">
													<div className="form-group mb-2">
														<label htmlFor="brand">Brand</label>
														<CustomDropdown
															type="addItem"
															title="brand"
															onItemClick={onItemClick}
															items={brands}
															selectedItem={inputData?.brand?.value || 'Select'}
															isDisabled={isBrandOrMarketEditable(
																'brand',
																'isBrandEditable'
															)}
														/>
														{errors?.brand && (
															<p className="error">{errors.brand}</p>
														)}
													</div>
												</div>
												<div className="col-md-6 pl-6">
													<div className="form-group mb-2">
														<label htmlFor="market">Market (Optional)</label>
														<CustomDropdown
															isDisabled={isBrandOrMarketEditable(
																'productGroup',
																'isProductGroupEditable'
															)}
															type="addItem"
															title="market"
															onItemClick={onItemClick}
															items={[{ id: null, value: 'Select' }].concat(
																markets
															)}
															selectedItem={
																inputData?.market?.value || 'Select'
															}
														/>
														{errors?.market && (
															<p className="error">{errors.market}</p>
														)}
													</div>
												</div>
											</>
										)}
										{(selectedBrands.length === 0 || !isShowAddBrands()) && (
											<>
												<div className="col-md-6 pr-6">
													<div className="form-group mb-2">
														<label htmlFor="target">Target</label>
														<input
															id="target"
															type="number"
															name="target"
															value={inputData?.target || ''}
															onKeyPress={validateFloatNumber}
															onChange={onChangeHandler}
															min={0}
														/>
														{errors?.target && (
															<p className="error">{errors.target}</p>
														)}
													</div>
													{(isAssignedClick || isDirector()) && (
														<p className="regular-14 gray_dark_02">
															Remaining Target:{' '}
															{getRemainingAmount(inputData?.target)}
														</p>
													)}
												</div>
												<div className="col-md-6 pl-6">
													<div className="form-group mb-2">
														<label htmlFor="nitexDesign">Nitex Design</label>
														<input
															id="nitexDesign"
															type="number"
															name="nitexDesign"
															value={inputData?.nitexDesign || ''}
															onChange={onChangeHandler}
															onKeyPress={validateNumber}
															min={0}
														/>
														{errors?.nitexDesign && (
															<p className="error">{errors.nitexDesign}</p>
														)}
													</div>
													{(isAssignedClick || isDirector()) && (
														<p className="regular-14 gray_dark_02">
															Remaining Design:{' '}
															{getRemainingDesign(inputData?.nitexDesign)}
														</p>
													)}
												</div>
											</>
										)}
									</div>
								</div>
							</div>

							{isShowAddBrands() && (
								<>
									{renderSelectedBrands()}
									<button
										className="button outline size32 ml-4"
										onClick={addNewBrand}
										disabled={!isAddBrandClickable()}
									>
										<img
											src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
											alt="add"
										/>
										<span>Add Brand</span>
									</button>
								</>
							)}
						</div>
						<div className="modal-footer justify-content-between">
							<div className="d-flex">
								<p className="regular-14 gray_dark_01">
									Total Target:{' '}
									<span className="semibold-16">
										{formatNumberWithSuffix(getTotalValue().target)}
									</span>
								</p>
								<p className="regular-14 gray_dark_03 mx-12">|</p>
								<p className="regular-14 gray_dark_01">
									Nitex Design:{' '}
									<span className="semibold-16">
										{convertNumberToUSFormat(getTotalValue().nitexDesign)}
									</span>
								</p>
							</div>
							<div className="d-flex gap-2">
								<button
									className="button outline size32"
									data-dismiss="modal"
									aria-label="Close"
									aria-hidden="true"
									onClick={clearFields}
								>
									Cancel
								</button>
								<button className="button size32" onClick={handleSubmit}>
									Set Target
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SetTarget;
